/**
 * The lobby's "Popular games" row: which slots it shows, and in what order.
 *
 * One rule for both readers of it. The player lobby draws the row
 * (apps/web/player/src/features/games/hot.ts); svc-game asks svc-art for the
 * tile of every game in it (apps/game/src/art/front-page.ts), so a game the row
 * shows is never left on its stand-in because the two picked differently.
 *
 * LEAF module like game-layers: imports nothing, so the player takes it
 * through `@b1fr0st/contracts/popular-games` without server dependencies.
 */

/** How many cards the row shows. */
export const POPULAR_GAMES_SHOWN = 12;

/** The catalog fields the rule reads. `rtp` arrives as a string from a numeric column, or as a number. */
export interface PopularCatalogGame {
  id: string;
  code: string;
  status?: string;
  rtp?: number | string | null;
  tag?: string | null;
}

/**
 * Catalog RTP as a percent for ordering: stored as a 0–1 fraction or as a
 * percent, as a number or a numeric string; -1 when unusable.
 */
export function catalogRtpPct(game: Pick<PopularCatalogGame, 'rtp'>): number {
  const n = game.rtp == null || game.rtp === '' ? Number.NaN : Number(game.rtp);
  if (!Number.isFinite(n) || n <= 0) return -1;
  return n <= 1 ? n * 100 : n;
}

/**
 * The cards to show: ranked games first, in the ranking's order, then catalog
 * slots by catalog RTP (then the `hot` tag) until `limit`. Ranked games the
 * catalog does not carry as active slots are dropped. svc-bet ranks by the game
 * CODE a bet was placed on, not the catalog row id, so the match is on `code`.
 * `slots` are in the catalog's own order (newest first), which breaks ties.
 */
export function pickPopularGames<G extends PopularCatalogGame, H extends { gameId: string }>(
  hot: readonly H[],
  slots: readonly G[],
  limit: number = POPULAR_GAMES_SHOWN,
): { game: G; ranked: H | null }[] {
  const active = slots.filter((g) => g.status === undefined || g.status === 'active');
  const byCode = new Map(active.map((g) => [g.code, g]));
  const ranked: { game: G; ranked: H | null }[] = [];
  for (const h of hot) {
    const game = byCode.get(h.gameId);
    if (game && ranked.length < limit) ranked.push({ game, ranked: h });
  }
  if (ranked.length >= limit) return ranked;

  const taken = new Set(ranked.map((r) => r.game.id));
  const fill = active
    .filter((g) => !taken.has(g.id))
    .sort((a, b) => catalogRtpPct(b) - catalogRtpPct(a) || Number(b.tag === 'hot') - Number(a.tag === 'hot'))
    .slice(0, limit - ranked.length)
    .map((game) => ({ game, ranked: null }));
  return [...ranked, ...fill];
}
